import { useRef, type KeyboardEvent, type ReactNode } from 'react';

import styles from './Tabs.module.css';

export interface TabItem {
  id: string;
  label: string;
}

export interface TabsProps {
  /** Names the tab list for a screen reader. */
  label: string;
  tabs: readonly TabItem[];
  selected: string;
  onSelect: (id: string) => void;
  /** The selected tab's panel. */
  children: ReactNode;
}

/** A row of tabs over one panel; arrow keys move between tabs. */
export function Tabs({ label, tabs, selected, onSelect, children }: TabsProps) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (step === 0) {
      return;
    }
    event.preventDefault();
    const next = tabs[(index + step + tabs.length) % tabs.length];
    onSelect(next.id);
    refs.current[next.id]?.focus();
  };

  return (
    <div className={styles.tabs}>
      <div aria-label={label} className={styles.list} role="tablist">
        {tabs.map((tab, index) => {
          const active = tab.id === selected;
          return (
            <button
              aria-controls={`panel-${tab.id}`}
              aria-selected={active}
              className={`${styles.tab} ${active ? styles.active : ''}`}
              id={`tab-${tab.id}`}
              key={tab.id}
              onClick={() => onSelect(tab.id)}
              onKeyDown={(event) => onKeyDown(event, index)}
              ref={(node) => {
                refs.current[tab.id] = node;
              }}
              role="tab"
              tabIndex={active ? 0 : -1}
              type="button"
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      <div
        aria-labelledby={`tab-${selected}`}
        className={styles.panel}
        id={`panel-${selected}`}
        role="tabpanel"
      >
        {children}
      </div>
    </div>
  );
}
